<template>
  <div :class="{ 'lg:tw-mt-[6.875rem]': !Settings.nameRightSide }">
    <loading-view-buttons v-if="panelLoading" rating-bar share-button />

    <div v-if="Settings.nameRightSide" class="tw-mb-8 tw-hidden lg:tw-block">
      <h3 class="tw-leading-tight tw-text-h3 tw-font-medium tw-mb-2">
        {{ mod?.name || '&nbsp;' }}
      </h3>
      <template v-if="mod">
        <p class="tw-opacity-70 tw-leading-none tw-mb-1">
          {{ $t('published') }}
          <base-time-tooltip :timestamp="mod.date_live">{{
            humanDateFormat(mod.date_live)
          }}</base-time-tooltip>
        </p>
        <p class="tw-opacity-70 tw-leading-none">
          {{ $t('updated') }}
          <base-time-tooltip :timestamp="mod.date_updated || mod.date_live">
            {{ timeSince(mod.date_updated || mod.date_live) }} {{ $t('ago') }}
          </base-time-tooltip>
        </p>
      </template>
    </div>

    <div v-if="mod && !panelLoading" :class="[Classes.BUTTONS, 'tw-space-y-2']">
      <subscribe-button
        v-if="Settings.allowSubscribing"
        :custom-class="`${Classes.DETAIL_SUBCRIBE_BUTTON} tw-mb-2`"
        :game="game"
        :mod="mod"
        :disabled="!hasFiles"
        :dependencies="dependencies"
      />

      <rating-buttons
        :liked="liked"
        :positive="ratings.positive"
        :negative="ratings.negative"
        :game="game"
        :mod="mod"
        @update:rating="update"
      />
    </div>
  </div>
</template>

<script>
import LoadingViewButtons from '@components/Loading/LoadingViewButtons.vue'
import { RATING_RESOURCE_ALREADY_RATED } from '@shared/js/config/errors.js'
import { humanDateFormat, isObjEmpty, timeSince } from '@helpers/utils.js'
import SubscribeButton from '@components/Mod/SubscribeButton.vue'
import RatingButtons from '@components/Mod/RatingButtons.vue'
import { RATINGS_UNDECIDED } from '@config/options.js'
import { updateModRatingRequest } from '@services'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { useAsync } from '@composables'
import { computed, toRefs } from 'vue'
import { ratingStore } from '@stores'

export default {
  components: {
    LoadingViewButtons,
    SubscribeButton,
    RatingButtons,
  },
  props: {
    mod: {
      type: Object,
      default: null,
    },
    game: {
      type: Object,
      default: null,
    },
    panelLoading: {
      type: Boolean,
      default: false,
    },
    dependencies: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { mod, game } = toRefs(props)

    const {
      errorRef: modRatingErrorRef,
      run: runUpdateModRating,
      error: modRatingError,
    } = useAsync((rating) =>
      updateModRatingRequest({
        gameId: game.value.id,
        modId: mod.value.id,
        rating,
      })
    )

    const {
      updateUserModRating,
      updateModRating,
      ratings: userRatings,
    } = ratingStore()

    const liked = computed(() => {
      if (userRatings.value && mod.value && game.value) {
        const rating = userRatings.value.find(
          (rating) =>
            rating.mod_id === mod.value.id && rating.game_id === game.value.id
        )

        if (rating) return rating.rating
      }

      return RATINGS_UNDECIDED
    })

    const ratings = computed(() => {
      return (
        mod.value && {
          text: mod.value.stats.ratings_display_text,
          negative: mod.value.stats.ratings_negative,
          positive: mod.value.stats.ratings_positive,
          percentage: mod.value.stats.ratings_percentage_positive,
        }
      )
    })

    const hasFiles = computed(
      () =>
        (mod.value?.modfile && !isObjEmpty(mod.value.modfile)) ||
        mod.value?.platforms?.length
    )

    async function update(update) {
      // change ratings on the frontend before submitting update request
      const currentRating = liked.value
      updateModRating({
        gameNameId: game.value.name_id,
        modNameId: mod.value.name_id,
        update,
        current: currentRating,
      })
      updateUserModRating(game.value.id, mod.value.id, update)

      await runUpdateModRating(update)

      if (modRatingError.value) {
        // if errors revert rating change
        updateModRating({
          gameNameId: game.value.name_id,
          modNameId: mod.value.name_id,
          update: currentRating,
          current: update,
        })
        if (modRatingErrorRef.value !== RATING_RESOURCE_ALREADY_RATED) {
          updateUserModRating(game.value.id, mod.value.id, currentRating)
        }
      }
    }

    return {
      humanDateFormat,
      timeSince,
      Settings,
      hasFiles,
      ratings,
      Classes,
      update,
      liked,
    }
  },
}
</script>
